// src/features/auth/AuthCacheManager.ts
import { db } from '../../lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useQueryClient } from '@tanstack/react-query';
import { authQueryKeys } from './queries/authQueries';
import { WorkspaceMembership, MemberInfo, AuthCacheData } from './types';
import { normalizeWorkspaceRole, normalizeWorkspaceStatus } from './utils/workspaceRoleStatus';

class AuthCacheManager {
  private cache: AuthCacheData;
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5分
  
  constructor() {
    this.cache = {
      subscribedWorkspaces: new Map(),
      subscribedMembers: new Map(),
      unsubscribedData: new Map(),
      subscriptions: new Map()
    };
  }
  
  /**
   * 購読中のデータを更新（有効期限なし）
   */
  updateSubscribedData(key: string, data: any) {
    this.cache.subscribedWorkspaces.set(key, data);
  }
  
  /**
   * 購読していないデータを有効期限付きで保存
   */
  setUnsubscribedData(key: string, data: any) {
    this.cache.unsubscribedData.set(key, {
      data,
      expiresAt: Date.now() + this.CACHE_TTL
    });
  }
  
  /**
   * データ取得時の判定
   */
  getData(key: string) {
    // 1. 購読中のデータを優先
    if (this.cache.subscribedWorkspaces.has(key)) {
      return this.cache.subscribedWorkspaces.get(key);
    }
    
    // 2. 購読していないデータを有効期限チェック付きで取得
    const unsubscribed = this.cache.unsubscribedData.get(key);
    if (unsubscribed && unsubscribed.expiresAt > Date.now()) {
      return unsubscribed.data;
    }
    
    return null; // キャッシュミス
  }
  
  /**
   * ワークスペースIDのリストから、各ワークスペースのメンバー情報を取得して権限を更新
   * React Query版: クエリキャッシュを直接更新
   */
  static async updateUserWorkspacesFromMembers(userId: string, workspaceIds: string[], queryClient?: any) {
    const updateStartTime = performance.now();

    const workspaces: WorkspaceMembership[] = [];

    // 並列取得用のPromise配列を作成
    const memberFetchPromises = workspaceIds.map(async (workspaceId) => {
      try {
        const memberFetchStartTime = performance.now();
        const memberDocRef = doc(db, `workspaces/${workspaceId}/members/${userId}`);
        const memberSnapshot = await getDoc(memberDocRef);
        const memberFetchEndTime = performance.now();
        
        if (memberSnapshot.exists()) {
          const memberData = memberSnapshot.data();

          const workspace: WorkspaceMembership = {
            workspaceId,
            role: normalizeWorkspaceRole(memberData.role),
            status: normalizeWorkspaceStatus(memberData.status),
            joinedAt: memberData.joinedAt?.toDate() || new Date(),
            lastChecked: Date.now()
          };

          return workspace;
        } else {
          console.warn('⚠️ [AuthCacheManager] メンバー情報が見つかりません:', {
            workspaceId,
            userId,
            fetchTime: memberFetchEndTime - memberFetchStartTime,
            totalElapsedTime: memberFetchEndTime - updateStartTime
          });
          return null;
        }
      } catch (error) {
        const errorTime = performance.now();
        console.error('❌ [AuthCacheManager] メンバー情報取得エラー:', {
          workspaceId,
          userId,
          error,
          elapsedTime: errorTime - updateStartTime
        });
        return null;
      }
    });

    // 並列実行
    const memberResults = await Promise.all(memberFetchPromises);

    // nullでない結果のみを追加
    memberResults.forEach(result => {
      if (result) {
        workspaces.push(result);
      }
    });

    // React Queryキャッシュを更新（優先）
    if (queryClient) {
      queryClient.setQueryData(authQueryKeys.userWorkspaces(userId), workspaces);
      
      // 個別のワークスペースアクセス権限も更新
      workspaces.forEach(workspace => {
        const hasAccess = workspace.status === 'active';
        queryClient.setQueryData(
          authQueryKeys.workspaceAccess(workspace.workspaceId, userId),
          hasAccess
        );
      });
    }

    const finalTime = performance.now();
    return workspaces;
  }

  /**
   * ユーザーのワークスペース一覧を更新（従来の方法 - 非推奨）
   * @deprecated React Query統合により非推奨
   */
  static updateUserWorkspaces(userId: string, snapshot: any, queryClient?: any) {
    console.warn('⚠️ AuthCacheManager.updateUserWorkspaces は非推奨です。React QueryのuseUserWorkspacesQueryを使用してください。');
    
    const workspaces: WorkspaceMembership[] = [];

    snapshot.docs.forEach((doc: any) => {
      const data = doc.data();
      const workspaceId = doc.id;

      const workspace: WorkspaceMembership = {
        workspaceId,
        role: normalizeWorkspaceRole(data.role),
        status: normalizeWorkspaceStatus(data.status),
        joinedAt: data.joinedAt?.toDate() || new Date(),
        lastChecked: Date.now()
      };

      workspaces.push(workspace);
    });

    // React Queryキャッシュを更新（優先）
    if (queryClient) {
      queryClient.setQueryData(authQueryKeys.userWorkspaces(userId), workspaces);
    }
    
    return workspaces;
  }
  
  /**
   * ワークスペースのメンバー一覧を更新
   * React Query版: 必要に応じてクエリキャッシュも更新
   */
  static updateWorkspaceMembers(workspaceId: string, snapshot: any, queryClient?: any) {
    const members = new Map<string, MemberInfo>();
    
    snapshot.docs.forEach((doc: any) => {
      const data = doc.data();
      const userId = doc.id;
      
      members.set(userId, {
        userId,
        role: normalizeWorkspaceRole(data.role),
        status: normalizeWorkspaceStatus(data.status),
        joinedAt: data.joinedAt?.toDate() || new Date()
      });
    });
    
    // React Queryキャッシュを更新（必要に応じて）
    if (queryClient) {
      // ワークスペースメンバー情報をキャッシュ
      queryClient.setQueryData(['workspaceMembers', workspaceId], members);
    }
    
    return members;
  }
  
  /**
   * 従来の方法でアクセス権限をチェック（フォールバック用）
   */
  static async checkAccessFromFirestore(workspaceId: string, userId: string): Promise<boolean> {
    try {
      const [workspaceMemberDoc, userWorkspaceDoc] = await Promise.all([
        getDoc(doc(db, `workspaces/${workspaceId}/members/${userId}`)),
        getDoc(doc(db, `users/${userId}/workspaces/${workspaceId}`))
      ]);
      
      const hasWorkspaceAccess = workspaceMemberDoc.exists();
      const hasUserWorkspace = userWorkspaceDoc.exists();
      
      return hasWorkspaceAccess && hasUserWorkspace;
    } catch (error) {
      console.error('❌ AuthCacheManager: アクセス権限チェックエラー:', error);
      return false;
    }
  }
  
  /**
   * キャッシュをクリア
   * React Query版: クエリキャッシュもクリア
   */
  clearCache(queryClient?: any) {
    this.cache.subscribedWorkspaces.clear();
    this.cache.subscribedMembers.clear();
    this.cache.unsubscribedData.clear();
    this.cache.subscriptions.clear();
    
    // React Queryキャッシュをクリア（優先）
    if (queryClient) {
      queryClient.removeQueries({ queryKey: authQueryKeys.all, exact: false });
    }
  }
  
  /**
   * 期限切れのキャッシュをクリーンアップ
   */
  cleanupExpiredCache() {
    const now = Date.now();
    
    // Map.entries()のイテレーションを修正
    const keysToDelete: string[] = [];
    this.cache.unsubscribedData.forEach((data, key) => {
      if (data.expiresAt <= now) {
        keysToDelete.push(key);
      }
    });
    
    keysToDelete.forEach(key => {
      this.cache.unsubscribedData.delete(key);
    });
  }
}

// シングルトンインスタンス
export const authCacheManager = new AuthCacheManager();

// クラスもエクスポート（必要に応じて）
export { AuthCacheManager };

// 定期的なクリーンアップ（5分ごと）
setInterval(() => {
  authCacheManager.cleanupExpiredCache();
}, 5 * 60 * 1000);
